import type { BaseIconProps } from "@/assets/icons/utils";
import { cn } from "@/components/podkit/lib/cn";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import * as React from "react";
import { createContext, useContext, useEffect, useImperativeHandle, useRef } from "react";

type TabsOrientation = TabsPrimitive.TabsProps["orientation"];

type TabsContextValue = {
    setOrientation: (orientation: TabsOrientation) => void;
};

const TabsContext = createContext<TabsContextValue | null>(null);

const Tabs = React.forwardRef<
    React.ElementRef<typeof TabsPrimitive.Root>,
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>
>(({ className, ...props }, forwardRef) => {
    const [orientation, setOrientationState] = React.useState<TabsOrientation>("horizontal");

    const setOrientation = (orientation: TabsOrientation) => {
        setOrientationState(orientation);
    };

    return (
        <TabsContext.Provider value={{ setOrientation }}>
            <TabsPrimitive.Root
                ref={forwardRef}
                orientation={orientation}
                className={cn("@container", className)}
                {...props}
            />
        </TabsContext.Provider>
    );
});

Tabs.displayName = TabsPrimitive.Root.displayName;

const TabsList = React.forwardRef<
    React.ElementRef<typeof TabsPrimitive.List>,
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, forwardRef) => {
    const ref = useRef<HTMLDivElement>(null);
    const tabsContext = useContext(TabsContext);

    useImperativeHandle(forwardRef, () => ref.current!);

    useEffect(() => {
        const observer = new ResizeObserver(() => {
            if (!ref.current || !(ref.current instanceof HTMLElement)) return;

            const isVertical = getComputedStyle(ref.current).flexDirection === "column";
            tabsContext?.setOrientation(isVertical ? "vertical" : "horizontal");
        });

        if (ref.current) {
            observer.observe(ref.current);
        }
        return () => {
            observer.disconnect();
        };
    }, [tabsContext]);

    return (
        <TabsPrimitive.List
            ref={ref}
            className={cn(
                "flex min-h-8 flex-col justify-stretch gap-0.5 overflow-hidden overflow-x-hidden rounded-lg border-0.5 border-border-base bg-surface-button-tab-base p-0.5 @md:flex-row @md:justify-between",
                className,
            )}
            {...props}
        />
    );
});
TabsList.displayName = TabsPrimitive.List.displayName;

type TabsTriggerProps = {
    LeadingIcon?: React.ComponentType<BaseIconProps>;
} & React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>;

const TabsTrigger = React.forwardRef<React.ElementRef<typeof TabsPrimitive.Trigger>, TabsTriggerProps>(
    ({ className, LeadingIcon, children, asChild, ...props }, ref) => {
        const iconElement = LeadingIcon ? <LeadingIcon size="sm" aria-hidden={true} /> : null;

        const triggerClassName = cn(
            "inline-flex flex-shrink flex-row h-8 min-w-0 flex-grow items-center @md:justify-center gap-2 overflow-hidden rounded-md border-transparent px-1.5 py-1 text-base text-content-strong text-left @md:text-center",
            // hover
            "hover:bg-surface-glass",
            // active
            "data-[state=active]:bg-surface-button-tab-primary data-[state=active]:border-transparent data-[state=active]:text-content-primary data-[state=active]:shadow-sm",
            // disabled
            "disabled:opacity-50 disabled:hover:bg-transparent",
            className,
        );

        if (asChild) {
            return (
                <TabsPrimitive.Trigger ref={ref} className={triggerClassName} asChild {...props}>
                    {children}
                </TabsPrimitive.Trigger>
            );
        }

        return (
            <TabsPrimitive.Trigger ref={ref} className={triggerClassName} {...props}>
                {iconElement}
                <span className="truncate">{children}</span>
            </TabsPrimitive.Trigger>
        );
    },
);
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
    React.ElementRef<typeof TabsPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
    <TabsPrimitive.Content
        ref={ref}
        className={cn(
            "ring-offset-background focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
            "data-[state=active]:duration-300 data-[state=active]:animate-in data-[state=active]:fade-in-0",
            className,
        )}
        {...props}
    />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsContent, TabsList, TabsTrigger };
export type { TabsTriggerProps };
